import { computed, ref } from "vue";
import { uuid } from "vue-uuid";
import { filesService } from "@/services/filesService";
import { DEFAULT_MAX_SIZE_BYTES, MAX_CHART_FILE_SIZE_BYTES, isPdfFile, parseUploadError, uploadService, validateFile, validatePdf, } from "@/services/uploadService";
import { tryParseApiErrors } from "@/utils";
/**
 * One upload path for chart documents: every file, PDF or scanner image, goes browser → S3, then the
 * prepare job converts images and compresses anything over the CMM budget. The per-file steps are
 * exported for uploaders that keep their own rows (the PA page's supporting-document slots); the
 * composable owns the import page's rows plus their picker / drag-and-drop handlers.
 */
// CoverMyMeds' attachment total per request; the backend clamps a prepare job to the same figure.
export const CMM_ATTACHMENT_BUDGET_BYTES = 10 * 1024 * 1024;
const PREPARE_POLL_INTERVAL_MS = 1000;
// Each matches a server bound: a job waits in the queue at most until it expires, then runs at most to its hard
// time limit. The poll counts the second window from the first "processing", not from the request.
const PREPARE_QUEUE_TIMEOUT_MS = 10 * 60 * 1000;
const PREPARE_RUN_TIMEOUT_MS = 10 * 60 * 1000;
const PDF_BROWSER_CHECK_MAX_BYTES = DEFAULT_MAX_SIZE_BYTES;
const PREPARE_POLL_MAX_CONSECUTIVE_FAILURES = 3;
const PREPARE_TIMEOUT_MESSAGE = "Compression is taking longer than expected. Please try again.";
const ROW_OCCUPIED_MESSAGE = "This document already has a file. Remove it first to upload a new one.";
const FREE_BUDGET_HINT = "Removing another document frees budget for this one.";
const MB = 1024 * 1024;
export const formatMb = (bytes) => (bytes / MB).toFixed(bytes < MB ? 2 : 1);
export const OVER_BUDGET_MESSAGE = `Together these documents exceed the ${formatMb(CMM_ATTACHMENT_BUDGET_BYTES)} MB attachment budget even at lowest quality. Remove a document or choose fewer pages.`;
const WATER_FILL_FLOOR_BYTES = MB;
/**
 * Splits the CMM budget across the documents of one form, the newly added one last: quotas are
 * origin-proportional and the rows pin in rounds — a row inside its quota (or at/under the floor, or
 * with no source to re-prepare from) keeps at its real size, a row whose quota falls under the
 * legibility floor pins at the floor, and every round re-shares what the pinned rows leave among the
 * rows still free. The rounds are what keeps the planned sizes adding up to the budget: the bytes a
 * floor adds to one row are paid by the other shares, never by the total. A plan whose pinned rows
 * alone exceed the budget comes back infeasible: the shares are only worth acting on while the set
 * can still fit, so both callers read that verdict before spending any quality.
 */
const planChartWaterFill = (entries, budgetBytes, floorBytes) => {
    const origins = entries.map((entry) => Math.max(1, entry.originBytes));
    const actions = entries.map((entry) => (entry.recompressible ? null : { kind: "keep" }));
    const plannedBytes = (index) => {
        const action = actions[index];
        if (!action)
            return 0;
        const { currentBytes } = entries[index];
        return action.kind === "keep" ? currentBytes : Math.min(currentBytes, action.targetBytes);
    };
    const totalPlannedBytes = () => entries.reduce((sum, _entry, index) => sum + plannedBytes(index), 0);
    for (;;) {
        const free = entries.map((_entry, index) => index).filter((index) => actions[index] === null);
        if (!free.length)
            break;
        const spareBytes = budgetBytes - totalPlannedBytes();
        const freeOrigin = free.reduce((sum, index) => sum + origins[index], 0);
        const shareOf = (index) => (spareBytes * origins[index]) / freeOrigin;
        const keepers = free.filter((index) => entries[index].currentBytes <= shareOf(index));
        if (keepers.length) {
            keepers.forEach((index) => {
                actions[index] = { kind: "keep" };
            });
            continue;
        }
        const underFloor = free.filter((index) => shareOf(index) < floorBytes);
        if (!underFloor.length) {
            free.forEach((index) => {
                actions[index] = { kind: "compress", targetBytes: Math.floor(shareOf(index)) };
            });
            break;
        }
        underFloor.forEach((index) => {
            actions[index] =
                entries[index].currentBytes <= floorBytes ? { kind: "keep" } : { kind: "compress", targetBytes: floorBytes };
        });
    }
    const settled = actions.map((action) => action ?? { kind: "keep" });
    return { feasible: totalPlannedBytes() <= budgetBytes, actions: settled };
};
export const emptyChartDoc = () => ({
    label: null,
    links: [],
    file: null,
    sourceUploadKey: null,
    originBytes: null,
    sizeBytes: null,
    level: null,
    pageCount: null,
    droppedPages: [],
    uploading: false,
    preparing: false,
    recompressing: false,
    stalledAtBytes: null,
    perPageBytes: null,
    error: null,
    errorDetails: null,
});
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const awaitPrepared = async (started) => {
    if (started.status !== "processing")
        return started;
    let deadline = Date.now() + PREPARE_QUEUE_TIMEOUT_MS;
    let running = false;
    let consecutiveFailures = 0;
    while (Date.now() < deadline) {
        await sleep(PREPARE_POLL_INTERVAL_MS);
        let result;
        try {
            result = await filesService.getPrepareDocumentStatus(started.taskId);
        }
        catch (pollFailure) {
            if (++consecutiveFailures >= PREPARE_POLL_MAX_CONSECUTIVE_FAILURES)
                throw pollFailure;
            continue;
        }
        consecutiveFailures = 0;
        if (result.status !== "queued" && result.status !== "processing")
            return result;
        if (result.status === "processing" && !running) {
            running = true;
            deadline = Date.now() + PREPARE_RUN_TIMEOUT_MS;
        }
    }
    return { status: "error", error: PREPARE_TIMEOUT_MESSAGE };
};
const toOutcome = (prepared, budgetBytes) => {
    if (prepared.status === "error") {
        return {
            status: "error",
            message: prepared.error,
            errorDetails: {
                achievedSize: prepared.achievedSize,
                pageCount: prepared.pageCount,
                pagesThatFit: prepared.pagesThatFit,
                floorBytesPerPage: prepared.floorBytesPerPage,
                budgetBytes,
                perPageBytes: prepared.perPageBytes,
                bestUploadKey: prepared.bestUploadKey,
                bestDownloadUrl: prepared.bestDownloadUrl,
            },
        };
    }
    return {
        status: "done",
        downloadUrl: prepared.downloadUrl,
        uploadKey: prepared.uploadKey,
        sizeBytes: prepared.compressedSize,
        level: prepared.level,
        pageCount: prepared.pageCount ?? null,
        droppedPages: prepared.droppedPages ?? [],
        pageRanges: prepared.pageRanges,
        perPageBytes: prepared.perPageBytes,
    };
};
/** The budget an outcome carries is the one the server judged the document against: it clamps the
 * figure the caller asked for, so the echo in the answer wins over the request. */
const runPrepare = async (started, budget) => toOutcome(await awaitPrepared(started), started.budgetBytes ?? budget.budgetBytes ?? CMM_ATTACHMENT_BUDGET_BYTES);
/** Validates and sends one chart file browser → S3. Throws with a user-facing message. */
export const uploadChartFile = async (file) => {
    const validationError = validateFile(file, { maxSizeBytes: MAX_CHART_FILE_SIZE_BYTES });
    if (validationError)
        throw new Error(validationError);
    if (isPdfFile(file, file.name) && file.size <= PDF_BROWSER_CHECK_MAX_BYTES)
        await validatePdf(file);
    return uploadService.uploadDirect(file, `${uuid.v4()}/${file.name}`, { chart: true });
};
/**
 * Runs the prepare job for a chart source key — a fresh upload's or one kept for re-preparation.
 * Every file goes through it, a PDF that already fits included: the job is what moves the object out
 * of the chart prefix into the attachable one, and a link still under the chart prefix is refused at
 * submit as an unprepared upload. A file needing no conversion costs nothing extra — the server
 * answers it inline.
 */
export const prepareChartFromKey = async (uploadKey, budget) => runPrepare(await filesService.prepareDocument(uploadKey, budget), budget);
/** The picker's confirm: a PDF from ``pages`` of the kept best attempt, compressed to the remaining budget. */
export const subsetChart = async (bestUploadKey, pages, budget) => runPrepare(await filesService.subsetPdf(bestUploadKey, pages, budget), budget);
export const failureMessage = (failure, hasOtherDocs) => hasOtherDocs ? `${failure.message} ${FREE_BUDGET_HINT}` : failure.message;
export const canChoosePagesFrom = (errorDetails) => Boolean(errorDetails?.bestDownloadUrl && errorDetails?.perPageBytes?.length);
const toSourcePages = (pages, droppedPages) => {
    if (!droppedPages.length)
        return pages;
    const dropped = new Set(droppedPages);
    const keptSourcePages = [];
    const highest = Math.max(...pages);
    for (let source = 1; keptSourcePages.length < highest; source++) {
        if (!dropped.has(source))
            keptSourcePages.push(source);
    }
    return pages.map((page) => keptSourcePages[page - 1]);
};
/** "pages 1-30 · 0.8 MB · 10 blank pages removed." — what the prepare job did to a document. */
export const preparedCaption = (prepared) => {
    const parts = [];
    if (prepared.pageRanges)
        parts.push(`pages ${prepared.pageRanges}`);
    parts.push(`${formatMb(prepared.sizeBytes)} MB`);
    const dropped = prepared.droppedPages.length;
    if (dropped)
        parts.push(`${dropped} blank ${dropped === 1 ? "page" : "pages"} removed`);
    return parts.join(" · ");
};
export function useChartUpload() {
    const docs = ref([emptyChartDoc()]);
    const fileInputRefs = ref({});
    const dragOverIndex = ref(null);
    let dragEnterCount = 0;
    const addDoc = () => docs.value.push(emptyChartDoc());
    const clearStalls = () => {
        docs.value.forEach((doc) => {
            doc.stalledAtBytes = null;
        });
    };
    const removeDoc = (index) => {
        docs.value.splice(index, 1);
        clearStalls();
        if (pickerIndex.value === index)
            closePicker();
        else if (pickerIndex.value !== null && pickerIndex.value > index)
            pickerIndex.value--;
        settleBudget();
    };
    const resetDocs = () => {
        docs.value = [emptyChartDoc()];
        dragOverIndex.value = null;
        dragEnterCount = 0;
        closePicker();
    };
    const hasUploadedDocs = () => docs.value.some((doc) => doc.links.length > 0);
    const isAnyUploadInProgress = () => docs.value.some((doc) => doc.uploading || doc.preparing || doc.recompressing);
    const documentLinks = () => docs.value.flatMap((doc) => doc.links);
    const otherFinalisedDocs = (doc) => docs.value.filter((other) => other !== doc && other.links.length > 0);
    const finalisedBytes = () => docs.value.reduce((sum, doc) => sum + (doc.links.length ? doc.sizeBytes ?? 0 : 0), 0);
    const isOverBudget = () => finalisedBytes() > CMM_ATTACHMENT_BUDGET_BYTES;
    const canChooseFromPrepared = (doc) => doc.links.length > 0 &&
        Boolean(doc.perPageBytes?.length) &&
        Boolean(doc.sourceUploadKey?.toLowerCase().endsWith(".pdf")) &&
        finalisedBytes() > CMM_ATTACHMENT_BUDGET_BYTES;
    const canChoosePages = (doc) => canChoosePagesFrom(doc.errorDetails) || canChooseFromPrepared(doc);
    const remainingBudget = (doc) => Math.max(0, CMM_ATTACHMENT_BUDGET_BYTES - otherFinalisedDocs(doc).reduce((sum, other) => sum + (other.sizeBytes ?? 0), 0));
    const stillListed = (doc) => docs.value.includes(doc);
    const budgetCaption = () => {
        const usedBytes = finalisedBytes();
        if (!usedBytes)
            return null;
        const leftBytes = Math.max(0, CMM_ATTACHMENT_BUDGET_BYTES - usedBytes);
        return `${formatMb(usedBytes)} MB of ${formatMb(CMM_ATTACHMENT_BUDGET_BYTES)} MB attachment budget used · ${formatMb(leftBytes)} MB left`;
    };
    let settlePending = false;
    // No message is part of this: a row's error text says nothing about whether it can shrink. A live
    // structured failure does — the picker is rendered off those details and a re-prepare drops them.
    const recompressible = (doc) => Boolean(doc.sourceUploadKey) && !doc.errorDetails && doc.sizeBytes !== doc.stalledAtBytes;
    // A never-finalised row whose prepare failed with a best attempt is worth retrying from its kept
    // original once the siblings free quota — unless its last retry already failed at this same
    // best-attempt size (the stall marker; clearStalls resets it on user action).
    const rescuable = (doc) => !doc.links.length &&
        Boolean(doc.sourceUploadKey) &&
        Boolean(doc.errorDetails?.achievedSize) &&
        doc.errorDetails?.achievedSize !== doc.stalledAtBytes;
    let prepareQueue = Promise.resolve();
    let pendingPrepares = 0;
    const queuePrepare = (job) => {
        pendingPrepares++;
        const queued = prepareQueue.then(job, job);
        prepareQueue = queued
            .catch(() => undefined)
            .then(() => {
            pendingPrepares--;
            if (pendingPrepares === 0)
                settleBudget();
        });
        return queued;
    };
    const onDragEnter = (index) => {
        dragEnterCount++;
        dragOverIndex.value = index;
    };
    const onDragLeave = () => {
        dragEnterCount--;
        if (dragEnterCount <= 0) {
            dragEnterCount = 0;
            dragOverIndex.value = null;
        }
    };
    const failWith = (doc, failure) => {
        doc.error = failureMessage(failure, otherFinalisedDocs(doc).length > 0);
        doc.errorDetails = failure.errorDetails;
    };
    const finalise = (doc, label, prepared) => {
        doc.error = null;
        doc.errorDetails = null;
        doc.links = [prepared.downloadUrl];
        doc.label = label;
        doc.sizeBytes = prepared.sizeBytes;
        doc.level = prepared.level ?? null;
        doc.pageCount = prepared.pageCount;
        doc.droppedPages = prepared.droppedPages;
        doc.perPageBytes = prepared.perPageBytes ?? null;
    };
    const reprepare = (doc, targetBytes) => {
        const sourceKey = doc.sourceUploadKey;
        if (!sourceKey)
            return;
        const beforeBytes = doc.sizeBytes ?? 0;
        doc.error = null;
        doc.errorDetails = null;
        doc.recompressing = true;
        if (pickerDoc.value === doc)
            closePicker();
        void queuePrepare(async () => {
            try {
                const outcome = await prepareChartFromKey(sourceKey, { budgetBytes: targetBytes, otherLinks: [] });
                if (!stillListed(doc))
                    return;
                if (outcome.status === "error") {
                    doc.errorDetails = outcome.errorDetails;
                    doc.stalledAtBytes = doc.sizeBytes;
                }
                else {
                    finalise(doc, doc.label ?? doc.file?.name ?? "document", outcome);
                    if ((doc.sizeBytes ?? 0) >= beforeBytes)
                        doc.stalledAtBytes = doc.sizeBytes;
                }
            }
            catch {
                doc.stalledAtBytes = doc.sizeBytes;
            }
            finally {
                doc.recompressing = false;
                if (settlePending)
                    settleBudget();
            }
        });
    };
    // What a retry may spend: the space the finalised siblings leave, minus the best-attempt sizes
    // reserved for the other rows still waiting on their own retry — without the reserve, the first
    // retry of a drain lands as large as fits and the next one re-fails a plan that was feasible.
    const rescueBudget = (doc) => Math.max(0, remainingBudget(doc) -
        docs.value
            .filter((other) => other !== doc && rescuable(other))
            .reduce((sum, other) => sum + (other.errorDetails?.achievedSize ?? 0), 0));
    /**
     * Retries a rescuable row from its kept original. The budget is read inside the queued job, after
     * the siblings' re-prepares ahead of it landed, so it measures the space they actually freed —
     * less the reserve for the other rows still waiting on their own retry; the
     * server returns the original untouched when it fits and otherwise compresses to the least
     * destructive level that does. A failed retry pins the stall marker at the new best attempt, so
     * the next drain does not re-fire it over an unchanged set.
     */
    const retryRescued = (doc) => {
        const sourceKey = doc.sourceUploadKey;
        if (!sourceKey)
            return;
        doc.preparing = true;
        if (pickerDoc.value === doc)
            closePicker();
        void queuePrepare(async () => {
            try {
                const outcome = await prepareChartFromKey(sourceKey, { budgetBytes: rescueBudget(doc), otherLinks: [] });
                if (!stillListed(doc))
                    return;
                if (outcome.status === "error") {
                    failWith(doc, outcome);
                    doc.stalledAtBytes = outcome.errorDetails.achievedSize ?? null;
                }
                else {
                    finalise(doc, doc.label ?? doc.file?.name ?? "document", outcome);
                }
            }
            catch (error) {
                doc.error = tryParseApiErrors(error)[0];
                doc.stalledAtBytes = doc.errorDetails?.achievedSize ?? null;
            }
            finally {
                doc.preparing = false;
                if (settlePending)
                    settleBudget();
            }
        });
    };
    /**
     * The final-state guard: a finalised set over the CMM total never settles silently. It runs at
     * every completion — queue drain, upload end, row removal — re-balancing by water-filling;
     * while work is still in flight it re-schedules itself (settlePending) instead of giving up.
     * A rescuable row joins the plan pinned at its best-attempt size: the siblings' shares pay for
     * it, and when the plan closes the row is retried over the freed space. When the plan cannot
     * close, the siblings still shrink to their shares and the row keeps its error and picker.
     * Every pass either shrinks a row or marks one stalled, so the re-prepares are bounded; when the
     * rows that can shrink cannot save the set, the newest row carries a visible over-budget error.
     */
    const settleBudget = () => {
        if (isAnyUploadInProgress()) {
            settlePending = true;
            return;
        }
        settlePending = false;
        const finalised = docs.value.filter((doc) => doc.links.length > 0);
        const rescued = docs.value.filter(rescuable);
        const overBudget = finalisedBytes() > CMM_ATTACHMENT_BUDGET_BYTES;
        if (!overBudget) {
            finalised.forEach((doc) => {
                if (doc.error === OVER_BUDGET_MESSAGE)
                    doc.error = null;
            });
            if (!rescued.length)
                return;
        }
        const plan = planChartWaterFill([
            ...finalised.map((doc) => ({
                originBytes: doc.originBytes ?? doc.sizeBytes ?? 0,
                currentBytes: doc.sizeBytes ?? 0,
                recompressible: recompressible(doc),
            })),
            ...rescued.map((doc) => ({
                originBytes: doc.originBytes ?? doc.errorDetails?.achievedSize ?? 0,
                currentBytes: doc.errorDetails?.achievedSize ?? 0,
                recompressible: false,
            })),
        ], CMM_ATTACHMENT_BUDGET_BYTES, WATER_FILL_FLOOR_BYTES);
        // The siblings' compress actions run on either verdict: feasible, they clear the space the
        // retry lands in; infeasible, they still shrink toward the floor, so the failed row's picker
        // meters against whatever space the set can actually free.
        finalised.forEach((doc, i) => {
            const action = plan.actions[i];
            if (action.kind === "compress")
                reprepare(doc, action.targetBytes);
        });
        if (!plan.feasible) {
            if (overBudget)
                finalised[finalised.length - 1].error = OVER_BUDGET_MESSAGE;
            return;
        }
        rescued.forEach(retryRescued);
    };
    /**
     * The budget a file's own prepare job targets, computed when the job starts: finalised siblings
     * count at their real size, still-queued ones at their origin size, so a burst of adds shares
     * the budget instead of each taking the whole remainder. The plan only sizes this file —
     * siblings re-prepare at queue drain, over real sizes.
     */
    const newFileBudget = (doc, fileSizeBytes) => {
        const siblings = docs.value.filter((other) => other !== doc && (other.links.length > 0 || other.preparing));
        const accountedBytes = (other) => (other.links.length ? other.sizeBytes ?? 0 : other.originBytes ?? 0);
        const remaining = CMM_ATTACHMENT_BUDGET_BYTES - siblings.reduce((sum, other) => sum + accountedBytes(other), 0);
        if (fileSizeBytes <= remaining)
            return remaining;
        const entries = [
            ...siblings.map((other) => ({
                originBytes: other.originBytes ?? accountedBytes(other),
                currentBytes: accountedBytes(other),
                // A still-queued sibling is about to be compressed by its own job; a finalised one only
                // counts as shrinkable under the same predicate the settle uses.
                recompressible: other.links.length === 0 || recompressible(other),
            })),
            { originBytes: fileSizeBytes, currentBytes: fileSizeBytes, recompressible: true },
        ];
        const plan = planChartWaterFill(entries, CMM_ATTACHMENT_BUDGET_BYTES, WATER_FILL_FLOOR_BYTES);
        const own = plan.actions[plan.actions.length - 1];
        if (!plan.feasible)
            return CMM_ATTACHMENT_BUDGET_BYTES;
        if (own.kind === "compress")
            return own.targetBytes;
        const plannedSiblingBytes = entries.slice(0, -1).reduce((sum, entry, index) => {
            const action = plan.actions[index];
            return sum + (action.kind === "keep" ? entry.currentBytes : Math.min(entry.currentBytes, action.targetBytes));
        }, 0);
        return Math.max(fileSizeBytes, CMM_ATTACHMENT_BUDGET_BYTES - plannedSiblingBytes);
    };
    const processFile = async (index, file) => {
        const doc = docs.value[index];
        doc.error = null;
        doc.errorDetails = null;
        if (!file)
            return;
        doc.uploading = true;
        let uploaded;
        try {
            uploaded = await uploadChartFile(file);
        }
        catch (error) {
            doc.uploading = false;
            doc.error = parseUploadError(error);
            return;
        }
        doc.uploading = false;
        clearStalls();
        // links stay unset until the document is final, so a preparing doc still reads as pending.
        doc.file = file;
        doc.sourceUploadKey = uploaded.uploadKey;
        doc.originBytes = file.size;
        doc.preparing = true;
        await queuePrepare(async () => {
            try {
                const outcome = await prepareChartFromKey(uploaded.uploadKey, {
                    budgetBytes: newFileBudget(doc, file.size),
                });
                if (!stillListed(doc))
                    return;
                if (outcome.status === "error")
                    failWith(doc, outcome);
                else
                    finalise(doc, file.name, outcome);
            }
            catch (error) {
                doc.error = tryParseApiErrors(error)[0];
            }
            finally {
                doc.preparing = false;
                if (settlePending)
                    settleBudget();
            }
        });
    };
    const pickerIndex = ref(null);
    const pickerDoc = computed(() => (pickerIndex.value === null ? null : docs.value[pickerIndex.value]));
    /** What the picker dialog shows: a failed prepare's best attempt wins — its figures describe what
     * actually fits — else the finalised row's prepared copy. Both meter against the space the other
     * documents leave under the attachment budget, which is the same figure the confirm's subset job
     * targets: the real constraint is the set fitting, not the fair share, and a meter that promised
     * more room than the job gets showed a selection as fitting and then failed it. */
    const pickerPropsFor = (doc) => {
        const details = doc.errorDetails;
        if (details?.bestDownloadUrl && details.perPageBytes?.length) {
            return {
                downloadUrl: details.bestDownloadUrl,
                perPageBytes: details.perPageBytes,
                budgetBytes: remainingBudget(doc),
                allowOverBudget: false,
            };
        }
        if (!canChooseFromPrepared(doc) || !doc.perPageBytes || !doc.links.length)
            return null;
        return {
            downloadUrl: doc.links[0],
            perPageBytes: doc.perPageBytes,
            budgetBytes: remainingBudget(doc),
            allowOverBudget: true,
        };
    };
    /** Frozen when the user opens the picker, because the dialog is rendered off these props: derived
     * live, they fell to null the moment a sibling's job brought the set under the budget — closing
     * the dialog mid-selection — and came back non-null, re-opening it with no click, as soon as the
     * set went over again. */
    const pickerProps = ref(null);
    const openPicker = (index) => {
        const props = pickerPropsFor(docs.value[index]);
        if (!props)
            return;
        pickerIndex.value = index;
        pickerProps.value = props;
    };
    const closePicker = () => {
        pickerIndex.value = null;
        pickerProps.value = null;
    };
    const chooseSubset = async (index, pages) => {
        const doc = docs.value[index];
        const bestUploadKey = doc.errorDetails?.bestUploadKey;
        const subsetKey = bestUploadKey ?? (doc.links.length ? doc.sourceUploadKey : null);
        if (!subsetKey)
            return;
        // The page choice is the row's identity from here on: the kept original leaves with it, so a
        // failed subset is never silently replaced by an unrequested full-document rescue at the drain.
        if (bestUploadKey && !doc.links.length)
            doc.sourceUploadKey = null;
        const subsetPages = bestUploadKey ? pages : toSourcePages(pages, doc.droppedPages);
        const name = doc.file?.name ?? "document";
        doc.error = null;
        doc.preparing = true;
        await queuePrepare(async () => {
            try {
                const outcome = await subsetChart(subsetKey, subsetPages, { budgetBytes: remainingBudget(doc) });
                if (!stillListed(doc))
                    return;
                if (outcome.status === "error") {
                    failWith(doc, outcome);
                }
                else {
                    finalise(doc, outcome.pageRanges ? `${name} (pages ${outcome.pageRanges})` : name, outcome);
                    doc.sourceUploadKey = null;
                }
            }
            catch (error) {
                doc.error = tryParseApiErrors(error)[0];
            }
            finally {
                doc.preparing = false;
                if (settlePending)
                    settleBudget();
            }
        });
    };
    const confirmPicker = async (pages) => {
        const index = pickerIndex.value;
        closePicker();
        if (index !== null)
            await chooseSubset(index, pages);
    };
    const acceptsFile = (doc) => {
        if (doc.links.length) {
            doc.error = ROW_OCCUPIED_MESSAGE;
            return false;
        }
        return !doc.uploading && !doc.preparing;
    };
    const handleFileDrop = async (index, event) => {
        dragOverIndex.value = null;
        dragEnterCount = 0;
        if (!acceptsFile(docs.value[index]))
            return;
        const file = event.dataTransfer?.files?.[0];
        if (file)
            await processFile(index, file);
    };
    const handleFileSelect = async (index, event) => {
        const input = event.target;
        const file = input.files?.[0];
        input.value = "";
        if (acceptsFile(docs.value[index]))
            await processFile(index, file);
    };
    return {
        docs,
        fileInputRefs,
        dragOverIndex,
        addDoc,
        removeDoc,
        resetDocs,
        hasUploadedDocs,
        isAnyUploadInProgress,
        isOverBudget,
        documentLinks,
        remainingBudget,
        budgetCaption,
        canChoosePages,
        pickerDoc,
        pickerProps,
        openPicker,
        closePicker,
        confirmPicker,
        chooseSubset,
        onDragEnter,
        onDragLeave,
        handleFileDrop,
        handleFileSelect,
        processFile,
    };
}
